import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface SearchResult {
  id: string;
  type: "patient";
  title: string;
  subtitle: string;
  route: string;
  icon: string;
  registrationNumber?: string;
  dateOfBirth?: string;
  gender?: string;
}

interface PatientSearchRow {
  id: string;
  first_name: string;
  last_name: string | null;
  name: string;
  phone: string;
  email: string | null;
  clinic_id: string;
  registration_number: string;
  date_of_birth: string | null;
  gender: string | null;
}

interface UseGlobalSearchOptions {
  query: string;
  enabled?: boolean;
}

export const useGlobalSearch = ({ query, enabled = true }: UseGlobalSearchOptions) => {
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const { user } = useAuth();

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const searchEnabled = enabled && debouncedQuery.length >= 2 && !!user;

  // Search patients - RLS enforces tenant isolation
  const patientsQuery = useQuery({
    queryKey: ["global-search", "patients", debouncedQuery],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)("search_patients", {
        p_query: debouncedQuery.trim(),
        p_limit: 10,
        p_offset: 0,
        p_clinic_ids: null,
      });
      
      if (error) throw error;
      return (data || []) as PatientSearchRow[];
    },
    enabled: searchEnabled,
  });

  // Format patient results
  const results = useMemo<SearchResult[]>(() => {
    if (!patientsQuery.data) return [];

    return patientsQuery.data.map((patient) => ({
      id: patient.id,
      type: "patient" as const,
      title: patient.name || `${patient.first_name} ${patient.last_name}`,
      subtitle: "View patient profile",
      route: `/patients/${patient.id}`,
      icon: "User",
      registrationNumber: patient.registration_number ?? undefined,
      dateOfBirth: patient.date_of_birth ?? undefined,
      gender: patient.gender ?? undefined,
    }));
  }, [patientsQuery.data]);

  return {
    results,
    isLoading: patientsQuery.isLoading,
    query: debouncedQuery,
  };
};
